import React, { useState } from "react";
import { RotateCw } from "lucide-react";

import { FunnelEvents, useDynamicEvents } from "@/api/insights";
import { Banner } from "@/design-system/components/Banner/Banner";
import { Button } from "@/design-system/components/Button/Button";
import { Typography } from "@/design-system/components/Typography/Typography";
import useCurrentProject from "@/hooks/use-project";
import { useHasRBACAccessToFeature } from "@/hooks/useHasRBACAccessToFeature";
import { unpackRequestErrorMessage } from "@/utils/request";
import { WizardStepLayout } from "@/wizards/framework/WizardStepLayout";
import {
  buildSavePayload,
  isSelectionStale,
  removedConversions,
  savedIds,
} from "../selection";
import { ConversionEventsStepProps } from "../types";
import { useSavedSelection } from "../useSavedSelection";
import { EventOptionsUnavailable } from "./EventOptionsStates";

function eventName(event: FunnelEvents) {
  return event.name || "Unnamed event";
}

function EventList({
  label,
  events,
  emptyText,
}: {
  label: string;
  events: FunnelEvents[];
  emptyText: string;
}) {
  return (
    <div className="flex flex-col gap-2">
      <Typography variant="label-sm">{label}</Typography>
      {events.length === 0 ? (
        <Typography variant="body-sm" color="subtle">
          {emptyText}
        </Typography>
      ) : (
        <ul className="border-borderSubtle divide-borderSubtle divide-y rounded-lg border">
          {events.map((event) => (
            <li
              key={event.eventDefId}
              className="text-label-sm text-textPrimary px-4 py-2"
            >
              {eventName(event)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ReviewStep({
  data,
  updateData,
  context,
  goToStep,
  complete,
}: ConversionEventsStepProps) {
  const project = useCurrentProject();
  const access = useHasRBACAccessToFeature("attribution");
  const {
    eventOptions,
    dynamicEventsData,
    eventsById,
    receivingEvents,
    retry,
  } = useSavedSelection({ data, updateData, context });
  const saveDynamicEvents = useDynamicEvents();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const payload =
    eventOptions.status === "ready" && dynamicEventsData
      ? buildSavePayload(data, eventsById, dynamicEventsData)
      : null;
  const stale =
    !!payload &&
    !!dynamicEventsData &&
    isSelectionStale(data, dynamicEventsData);
  const removed =
    payload && dynamicEventsData
      ? removedConversions(data, dynamicEventsData)
      : [];
  const projectName = project.loaded ? project.name : "this project";

  function reloadSaved() {
    if (!dynamicEventsData) {
      return;
    }
    const ids = savedIds(dynamicEventsData);
    updateData({
      ...ids,
      seededBottomFunnelIds: ids.bottomFunnelIds,
      seededMidFunnelIds: ids.midFunnelIds,
    });
  }

  async function save() {
    if (!payload) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveDynamicEvents(payload);
      const enrichedIds = new Set(
        (context.enriched.data ?? []).map((event) => event.eventDefId),
      );
      updateData({
        savedBottomFunnelNames: payload.bottomFunnel.map(eventName),
        savedIncludesNewEvents:
          !context.enriched.isError &&
          [...payload.bottomFunnel, ...payload.midFunnel].some(
            (event) => !enrichedIds.has(event.eventDefId),
          ),
      });
      complete();
    } catch (saveError) {
      setError(
        unpackRequestErrorMessage(saveError) ||
          "Couldn’t save your conversions. Try again.",
      );
      setSaving(false);
    }
  }

  let disabledReason: string | undefined;
  if (eventOptions.status === "loading") {
    disabledReason = "Loading your conversions…";
  } else if (eventOptions.status === "error") {
    disabledReason = "Couldn’t load your events. Select Try again, then save.";
  } else if (access.loading) {
    disabledReason = "Checking your permissions…";
  } else if (access.level !== "write") {
    disabledReason = "Ask an admin for permission to edit conversions";
  } else if (!payload || payload.bottomFunnel.length === 0) {
    disabledReason = "Choose a low-funnel event first";
  }

  return (
    <WizardStepLayout
      title="Review and save"
      description={`Conversions apply to every environment in ${projectName}, across Attribution, Journeys and Freshpaint AI.`}
      primaryAction={{
        label: "Save conversions",
        onClick: save,
        loading: saving,
        disabled: disabledReason !== undefined,
        disabledReason,
      }}
    >
      {payload ? (
        <>
          {stale && (
            <Banner type="warning">
              <div className="flex flex-col items-start gap-2">
                <span>
                  Conversions changed since you started. Reload them to start
                  from what’s saved now, or save to replace them with your
                  choices.
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  leadingIcon={<RotateCw aria-hidden />}
                  onClick={reloadSaved}
                >
                  Reload saved conversions
                </Button>
              </div>
            </Banner>
          )}
          <EventList
            label="Low-funnel"
            events={payload.bottomFunnel}
            emptyText="None yet. Go back and choose at least one."
          />
          <EventList
            label="Mid-funnel"
            events={payload.midFunnel}
            emptyText="None"
          />
          {removed.length > 0 && (
            <Typography variant="body-sm" color="subtle">
              Saving stops counting these events:{" "}
              {removed.map(eventName).join(", ")}
            </Typography>
          )}
        </>
      ) : (
        eventOptions.status !== "ready" && (
          <EventOptionsUnavailable
            eventOptions={eventOptions}
            receivingEvents={receivingEvents}
            retry={retry}
          />
        )
      )}
      <Typography variant="body-sm" color="subtle">
        Want to change something?{" "}
        <button
          type="button"
          className="text-textActionSelected cursor-pointer underline"
          onClick={() => goToStep("low-funnel")}
        >
          Edit your choices
        </button>
        .
      </Typography>
      {!access.loading && access.level !== "write" && (
        <Banner type="warning">
          You don’t have permission to edit conversions. Ask an admin to save
          them.
        </Banner>
      )}
      {error && <Banner type="error">{error}</Banner>}
    </WizardStepLayout>
  );
}
